import dayjs from 'dayjs';
import { isEmpty, isNil } from 'lodash';

import { SHORT_MONTH_DAY_YEAR_FORMAT } from 'constants/date';

import {
  AgencyFieldSuffix,
  APPROVAL_AGENCY_ROWS,
  APPROVAL_SCOPE_FOOTER_NO_DATE,
  APPROVAL_SCOPE_FOOTER_PREFIX,
} from './approvalScope.constants';
import { ApprovalStatus } from './CommandCenterHome.constants';

const getAgencyApproved = (agencyApprovals, agencyKey) =>
  !!agencyApprovals?.[`${agencyKey}${AgencyFieldSuffix.APPROVED}`];

const getApprovalStatus = isApproved =>
  isApproved ? ApprovalStatus.APPROVED : ApprovalStatus.NOT_APPROVED;

// Maps the per-subcompany agency-approval scalar group into homepage rows.
// Conventional collapses Fannie/Freddie (approved when either GSE is approved).
export const mapApprovalScopePrograms = agencyApprovals =>
  APPROVAL_AGENCY_ROWS.map(({ id, label, agencyKeys }) => ({
    id,
    label,
    status: getApprovalStatus(
      agencyKeys.some(agencyKey => getAgencyApproved(agencyApprovals, agencyKey)),
    ),
  }));

// Joins the full channel catalog to the subcompany's active channel set so the
// card can render approved-vs-excluded — the raw subcompany endpoint returns only
// the approved subset, so the catalog is what yields "Not approved".
export const mapApprovalScopeChannels = (channelCatalog = [], subCompanyChannels = []) => {
  const activeChannelIds = new Set(subCompanyChannels.map(({ channelId }) => channelId));

  return channelCatalog.map(({ channelId, name }) => ({
    id: channelId,
    label: name,
    status: getApprovalStatus(activeChannelIds.has(channelId)),
  }));
};

// Derives the "effective <date>" footer from the latest non-null agency ApprovalDate.
export const getApprovalScopeFooterLabel = agencyApprovals => {
  if (isNil(agencyApprovals)) {
    return APPROVAL_SCOPE_FOOTER_NO_DATE;
  }

  const approvalDates = APPROVAL_AGENCY_ROWS.flatMap(({ agencyKeys }) => agencyKeys)
    .map(agencyKey => agencyApprovals[`${agencyKey}${AgencyFieldSuffix.APPROVAL_DATE}`])
    .filter(date => !isNil(date))
    .map(date => dayjs(date));

  if (isEmpty(approvalDates)) {
    return APPROVAL_SCOPE_FOOTER_NO_DATE;
  }

  const latest = approvalDates.reduce((max, date) => (date.isAfter(max) ? date : max));

  return `${APPROVAL_SCOPE_FOOTER_PREFIX} · effective ${latest.format(
    SHORT_MONTH_DAY_YEAR_FORMAT,
  )}`;
};

// Reshapes the three raw responses into the card's display model. `isEmpty` is true
// when neither agency approvals nor active channels are on file (correct for the
// global test tenant, which has no broker-scoped approval data).
export const mapApprovalScope = ({ agencyApprovals, subCompanyChannels, channelCatalog }) => {
  const channels = mapApprovalScopeChannels(channelCatalog, subCompanyChannels);
  const programs = mapApprovalScopePrograms(agencyApprovals);

  return {
    channels,
    programs,
    footerLabel: getApprovalScopeFooterLabel(agencyApprovals),
    isEmpty: isNil(agencyApprovals) && isEmpty(subCompanyChannels),
  };
};
